import { CodeBlock, SectionTitle, SubTitle, InfoBox, Badge } from './shared';

export const OverviewContent = () => {
  return (
    <div className="max-w-4xl mx-auto px-8 md:px-16 py-12">
      <h1 className="text-4xl font-bold text-white mb-4">智能体概述</h1>
      <p className="text-base text-slate-300 mb-8 leading-relaxed">
        智能体是 Goose 的核心组件，负责理解用户意图、规划任务、调用工具并生成响应。本节将介绍智能体的架构、工作原理以及如何配置和使用。
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-12">
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 hover:border-cyan-500/30 transition-all">
          <div className="w-12 h-12 bg-cyan-500/10 rounded-xl flex items-center justify-center mb-4">
            <i className="ri-brain-line text-2xl text-cyan-400"></i>
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">智能推理</h3>
          <p className="text-base text-slate-400">
            基于大语言模型的智能推理能力，理解复杂任务并制定执行计划
          </p>
        </div>

        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 hover:border-cyan-500/30 transition-all">
          <div className="w-12 h-12 bg-purple-500/10 rounded-xl flex items-center justify-center mb-4">
            <i className="ri-tools-line text-2xl text-purple-400"></i>
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">工具调用</h3>
          <p className="text-base text-slate-400">
            支持多种工具和技能，可以执行文件操作、代码运行、网络请求等任务
          </p>
        </div>

        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 hover:border-cyan-500/30 transition-all">
          <div className="w-12 h-12 bg-emerald-500/10 rounded-xl flex items-center justify-center mb-4">
            <i className="ri-loop-left-line text-2xl text-emerald-400"></i>
          </div>
          <h3 className="text-lg font-semibold text-white mb-2">持续学习</h3>
          <p className="text-base text-slate-400">
            通过会话记忆和上下文管理，智能体可以在对话中不断学习和改进
          </p>
        </div>
      </div>

      <SectionTitle>核心特性</SectionTitle>
      
      <div className="space-y-5 mb-12">
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-cyan-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-message-3-line text-xl text-cyan-400"></i>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-white mb-2">多轮对话</h4>
              <p className="text-base text-slate-300 leading-relaxed">
                支持多轮对话，智能体可以记住之前的对话内容，提供连贯的交互体验。通过会话管理机制，智能体能够维护对话上下文，理解用户的连续指令。
              </p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-purple-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-git-branch-line text-xl text-purple-400"></i>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-white mb-2">任务规划</h4>
              <p className="text-base text-slate-300 leading-relaxed">
                智能体可以将复杂任务分解为多个子任务，并按照合理的顺序执行。支持任务依赖关系管理，确保任务按正确的顺序完成。
              </p>
            </div>
          </div>
        </div>

        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 bg-emerald-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-shield-check-line text-xl text-emerald-400"></i>
            </div>
            <div>
              <h4 className="text-lg font-semibold text-white mb-2">安全沙箱</h4>
              <p className="text-base text-slate-300 leading-relaxed">
                所有工具调用都在安全沙箱环境中执行，保护系统安全。支持权限控制和资源限制，防止恶意操作。
              </p>
            </div>
          </div>
        </div>
      </div>

      <SectionTitle>工作流程</SectionTitle>
      
      <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 mb-12">
        <div className="space-y-4">
          {[
            { step: 1, title: '接收用户输入', desc: '智能体接收用户的自然语言指令', icon: 'ri-user-voice-line', color: 'cyan' },
            { step: 2, title: '理解意图', desc: '使用大语言模型分析用户意图和任务需求', icon: 'ri-lightbulb-line', color: 'purple' },
            { step: 3, title: '规划任务', desc: '将任务分解为可执行的步骤，选择合适的工具', icon: 'ri-route-line', color: 'emerald' },
            { step: 4, title: '执行操作', desc: '调用相应的工具和技能完成任务', icon: 'ri-play-circle-line', color: 'amber' },
            { step: 5, title: '生成响应', desc: '整合执行结果，生成自然语言响应返回给用户', icon: 'ri-chat-check-line', color: 'cyan' },
          ].map((item) => (
            <div key={item.step} className="flex items-start gap-4">
              <div className={`w-10 h-10 bg-${item.color}-500/10 rounded-lg flex items-center justify-center flex-shrink-0`}>
                <i className={`${item.icon} text-xl text-${item.color}-400`}></i>
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-1">
                  <span className={`px-3 py-1 bg-${item.color}-500/10 text-${item.color}-400 text-sm font-medium rounded-full`}>
                    步骤 {item.step}
                  </span>
                  <h4 className="text-base font-semibold text-white">{item.title}</h4>
                </div>
                <p className="text-base text-slate-300">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      <InfoBox type="info">
        智能体的性能和能力很大程度上取决于所使用的大语言模型。建议根据任务复杂度选择合适的模型，并配置合理的参数。
      </InfoBox>

      <SectionTitle>快速开始</SectionTitle>
      
      <p className="text-base text-slate-300 mb-5 leading-relaxed">
        要开始使用智能体，您需要先配置模型提供商和 API 密钥。以下是基本配置步骤：
      </p>

      <CodeBlock language="bash">
{`# 配置 OpenAI API 密钥
goose config set openai.api_key YOUR_API_KEY

# 启动智能体会话
goose session start

# 与智能体交互
goose chat "帮我分析这个项目的代码结构"`}
      </CodeBlock>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-8">
        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 hover:border-cyan-500/30 transition-all cursor-pointer">
          <div className="flex items-center gap-3 mb-3">
            <i className="ri-settings-3-line text-2xl text-cyan-400"></i>
            <h4 className="text-lg font-semibold text-white">配置智能体</h4>
          </div>
          <p className="text-base text-slate-400 mb-3">
            了解如何配置智能体的各项参数，包括模型选择、温度设置、最大令牌数等
          </p>
          <span className="text-sm text-cyan-400 flex items-center gap-1">
            查看配置指南 <i className="ri-arrow-right-line"></i>
          </span>
        </div>

        <div className="bg-slate-800/40 border border-slate-700/50 rounded-xl p-6 hover:border-cyan-500/30 transition-all cursor-pointer">
          <div className="flex items-center gap-3 mb-3">
            <i className="ri-code-box-line text-2xl text-purple-400"></i>
            <h4 className="text-lg font-semibold text-white">开发技能</h4>
          </div>
          <p className="text-base text-slate-400 mb-3">
            学习如何为智能体开发自定义技能，扩展其功能和能力
          </p>
          <span className="text-sm text-purple-400 flex items-center gap-1">
            查看开发文档 <i className="ri-arrow-right-line"></i>
          </span>
        </div>
      </div>
    </div>
  );
};

export default OverviewContent;
